/**
 * Authentication Service
 *
 * Handles all authentication-related API calls to the backend Lambda.
 * This service manages the ISAC authentication flow including:
 * - Starting the Builder ID login flow
 * - Finishing authentication with authcode
 * - Validating and refreshing sessions
 *
 * Requirements:
 * - 1.1: Initiate ISAC StartFederation
 * - 1.3: Exchange authcode for SessionToken
 * - 2.1: Send SessionToken cookie automatically
 * - 2.2: Validate SessionToken with backend
 */

import {
  StartFederationResponse,
  FinishFederationRequest,
  GetSessionResponse,
} from '../types/auth';
import { PortalError } from './customerError';

interface AppSettings {
  apiBaseUrl: string;
  region: string;
  stage: string;
}

let settings: AppSettings | null = null;

function normalizeAuthError(error: unknown): PortalError {
  if (error instanceof PortalError) return error;
  if (error instanceof TypeError) return new PortalError('network', true);
  if (error instanceof SyntaxError) return new PortalError('invalidResponse', true);
  console.error('Unexpected authentication client failure', error);
  return new PortalError('unexpected');
}

/**
 * Load application settings from settings.json
 */
async function loadSettings(): Promise<AppSettings> {
  if (settings) {
    return settings;
  }

  try {
    const response = await fetch('/settings.json');
    if (!response.ok) {
      console.error('Application settings request failed', {
        status: response.status,
        statusText: response.statusText,
      });
      throw new PortalError('server', true);
    }
    settings = await response.json();
    return settings!;
  } catch (error) {
    throw normalizeAuthError(error);
  }
}

/**
 * Get the API base URL from settings.json
 */
const getApiBaseUrl = async (): Promise<string> => {
  const appSettings = await loadSettings();
  if (!appSettings.apiBaseUrl || appSettings.apiBaseUrl === 'PLACEHOLDER_API_URL') {
    console.error('Application settings do not contain a usable API base URL');
    throw new PortalError('invalidResponse');
  }
  // Strip trailing slash to prevent double-slash in URLs
  return appSettings.apiBaseUrl.replace(/\/+$/, '');
};

/**
 * Start the login flow by calling backend /auth/start
 * Backend initiates ISAC StartFederation and returns redirect URL
 *
 * Requirements:
 * - 1.1: Backend initiates ISAC StartFederation with Application ARN
 * - 1.2: Backend returns redirect parameters for Builder ID
 *
 * @param state - Random state string for CSRF protection
 * @returns Promise with redirect URL and parameters
 * @throws Error if the request fails or network error occurs
 */
export async function startLogin(state: string): Promise<StartFederationResponse> {
  try {
    const apiBaseUrl = await getApiBaseUrl();
    const redirectUrl = `${window.location.origin}/callback`;
    const response = await fetch(`${apiBaseUrl}/auth/start-federation`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ redirectUrl, state }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`startLogin failed: ${response.status} ${response.statusText}. ${errorText}`);
      if (response.status === 401 || response.status === 403) {
        throw new PortalError('authenticationFailed');
      } else if (response.status >= 500) {
        throw new PortalError('server', true);
      } else {
        throw new PortalError('authenticationFailed');
      }
    }

    const data: StartFederationResponse = await response.json();
    return data;
  } catch (error) {
    throw normalizeAuthError(error);
  }
}

/**
 * Finish the login flow by exchanging authcode for SessionToken
 * Backend calls ISAC FinishFederation and sets SessionToken in httpOnly cookie
 *
 * Requirements:
 * - 1.3: Backend receives authcode from Builder ID callback
 * - 1.4: Backend exchanges authcode for tokens via ISAC FinishFederation
 * - 1.5: Backend stores SessionToken in httpOnly cookie
 *
 * @param authcode - Authorization code from Builder ID callback
 * @param state - State parameter for CSRF validation
 * @returns Promise that resolves when authentication is complete
 * @throws Error if the request fails or network error occurs
 */
export async function finishLogin(authcode: string, state: string): Promise<void> {
  try {
    const apiBaseUrl = await getApiBaseUrl();
    const requestBody: FinishFederationRequest = {
      authCode: authcode,
      state,
    };

    const response = await fetch(`${apiBaseUrl}/auth/finish-federation`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`finishLogin failed: ${response.status} ${response.statusText}. ${errorText}`);
      if (response.status === 401 || response.status === 403) {
        throw new PortalError('authenticationFailed');
      } else if (response.status >= 500) {
        throw new PortalError('server', true);
      } else {
        throw new PortalError('authenticationFailed');
      }
    }

    // SessionToken is set as httpOnly cookie by the backend response.
    // No need to extract or store it — the browser handles it automatically.
  } catch (error) {
    throw normalizeAuthError(error);
  }
}

/**
 * Get the current session by validating SessionToken cookie
 * Backend validates SessionToken with ISAC and returns AWS credentials
 *
 * Requirements:
 * - 2.1: SessionToken sent automatically via httpOnly cookie
 * - 2.2: Backend validates SessionToken with ISAC GetSession
 * - 2.3: Backend returns AWS credentials and user email
 * - 2.4: Returns 401 if SessionToken is invalid or expired
 *
 * @returns Promise with AWS credentials and user email
 * @throws Error if session is invalid, expired, or network error occurs
 */
export async function getSession(): Promise<GetSessionResponse> {
  try {
    const apiBaseUrl = await getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/auth/get-session`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 401) {
      throw new PortalError('sessionExpired');
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`getSession failed: ${response.status} ${response.statusText}. ${errorText}`);
      if (response.status >= 500) {
        throw new PortalError('server', true);
      }
      throw new PortalError('sessionExpired');
    }

    const data: GetSessionResponse = await response.json();
    return data;
  } catch (error) {
    throw normalizeAuthError(error);
  }
}

/**
 * Logout by revoking the ISAC session token.
 * Backend calls ISAC RevokeSession and clears the SessionToken cookie.
 */
export async function logout(): Promise<void> {
  try {
    const apiBaseUrl = await getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      console.error('Logout failed', { status: response.status, statusText: response.statusText });
      throw new PortalError(
        response.status >= 500 ? 'server' : 'unexpected',
        response.status >= 500,
      );
    }
  } catch (error) {
    throw normalizeAuthError(error);
  }
}

/**
 * Authentication service object
 * Provides a convenient interface for all authentication operations
 */
export const authService = {
  startLogin,
  finishLogin,
  getSession,
  logout,
};
